import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, NgIf, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { RoutePaths } from '@helper/route-paths';
import { SearchModel } from '../../search/search.model';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { RouterHistoryService } from '@service/router-history.service';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';

@Component({
  selector: 'app-header-bar',
  templateUrl: './header-bar.component.html',
  styleUrls: ['./header-bar.component.scss'],
  imports: [NgIf, NgTemplateOutlet, ImageLoadingComponent, FormsModule, FocusRemoverDirective],
  providers: [SearchModel],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderBarComponent {
  static height = 47.5;

  readonly searchUrl = `/${RoutePaths.SEARCH}`;
  readonly height = HeaderBarComponent.height;
  readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly router = inject(Router);
  private readonly focusDelay = 300;

  @Input({ required: true }) lastPageUrl = '';
  @Input() title = '';
  @Input() iconTemplate?: TemplateRef<any>;
  @Input() textTemplate?: TemplateRef<any>;
  @Input() template?: TemplateRef<any>;
  @Input() customBack = false;
  @Input() showBack = true;
  @Input() isShopWindow = false;

  // search mode
  @Input() searchMode = false;
  @Input() goBackSearch = false;
  @Input() placeholder = '';
  @Input() keyword = '';
  @Input() disabled?: boolean;
  @Input() showDivider = false;
  @Input() marginRight = 35;

  @Output() readonly keywordChanged = new EventEmitter<string>();
  @Output() readonly searchByKeyword = new EventEmitter<string>();
  @Output() readonly back = new EventEmitter<void>();

  @ViewChild('input') input?: ElementRef;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private routerHistoryService: RouterHistoryService
  ) {
    afterNextRender(() =>
      setTimeout(() => {
        if (!this.keyword) {
          this.input?.nativeElement?.focus();
        }
      }, this.focusDelay)
    );
  }

  onBack(): void {
    if (this.customBack) {
      this.back.emit();
    } else if (!this.lastPageUrl) {
      this.routerHistoryService.goBack();
    } else {
      this.routerHistoryService.goRoute(this.lastPageUrl);
    }
  }

  onKeywordChange(keyword: string, input?: HTMLInputElement): void {
    const v = keyword.replaceAll('%', '');
    if (input) {
      input.value = v;
    }
    this.keywordChanged.emit(v);
  }

  toSearchPage(): void {
    if (!this.disabled) {
      return;
    }
    if (this.goBackSearch) {
      // 如果在 search result, 點搜尋框, 都是回到 search 頁, 屬於 back.
      this.routerHistoryService.goRoute(this.searchUrl);
    } else {
      // 其他頁面, 點搜尋框, 都是前往 search 頁, 屬於 navigate.
      this.router.navigate([RoutePaths.SEARCH]);
    }
  }

  onSearch(keyword: string): void {
    this.searchByKeyword.emit(keyword);
  }
}
